import type PublicTransition from '@ember/routing/transition';
import { later } from '@ember/runloop';
import { service } from '@ember/service';

import type { APIData, Service } from 'webapp-types';
import { BatchSelectActionType, MetricFilterOperator, ModelName } from 'webapp/enums/enums';

import type DateRange from 'webapp/models-custom/date-range';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import type MetricSetup from 'webapp/models-custom/metric-setup';
import type HorizonMetricRecord from 'webapp/models/horizon-metric';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { BatchSelectActionOptions } from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

// Horizon's verdict on a block's splits. `breakdowns` is the list sent, in that order: a refused pick comes back
// at `breakdowns[i]`. No options list (the slot isn't ready) narrows nothing; an empty list means nothing else fits.
// Options Horizon marks unconfirmed stay offered: the definition names them, Horizon just couldn't check them.
export function toBreakdownOffer(
	response: APIData.HorizonMetrics.ResolveFiltersResponse,
	breakdowns: string[],
): HorizonBreakdownOffer {
	const refused = new Set<string>();
	(response.invalidPicks || []).forEach(({ slot }) => {
		const match = /^breakdowns\[(\d+)]$/.exec(slot);
		const column = match ? breakdowns[Number(match[1])] : undefined;
		if (column) {
			refused.add(column);
		}
	});
	const options = response.dimensions?.options;
	const addable = options ? new Set(options.map(({ column }) => column)) : null;
	return { addable, refused };
}

export interface HorizonBreakdownOffer {
	addable: Set<string> | null; // splits the block may still add; null when Horizon offers none (no narrowing)
	refused: Set<string>; // picked splits Horizon refuses together
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/horizon-metrics': ModelManagerHorizonMetricsService;
	}
}

export default class ModelManagerHorizonMetricsService extends ModelManagerForStoreService<
	HorizonMetricRecord,
	HorizonMetric
> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get horizonMetricModels(): HorizonMetric[] {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	requestHorizonMetrics(): Promise<void> {
		const variablePrefix = 'horizonMetrics';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.HORIZON_METRIC);
			},
		);
	}

	modelName = ModelName.HORIZON_METRIC;
	// Horizon is internal; users see Built metrics.
	override modelTitle = 'Built metric';

	createModel(model: HorizonMetricRecord): HorizonMetric {
		return new HorizonMetric({ model });
	}

	openEditPopupWithTransition(model: HorizonMetric): PublicTransition {
		// The id, not the record: given a record, the route skips its model hook, which looks up the metric.
		return this.navigationService.transitionToRoute('metrics.edit.horizon', model.idForSure);
	}

	openEditPopup(
		model: HorizonMetric,
		origin: TrackingOriginForMetricEdit,
		afterSave?: () => void,
	) {
		const popup = this.mmMetricsService.createCustomMetricEditPopup();
		popup.open({
			model,
			origin,
			afterSave,
		});
		return popup;
	}

	openViewPopup(model: HorizonMetric, origin: TrackingOriginForMetricEdit) {
		const popup = this.mmMetricsService.createCustomMetricEditPopup();
		popup.open({
			model,
			origin,
			viewOnly: true,
		});
		return popup;
	}

	createBlockFromHorizonMetric(model: HorizonMetric): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const metricSetup = model.metricSetup;
		if (!metricSetup.key) {
			return;
		}

		this.mmBlocksService.openCreateFromMetricSetupPopup({
			metricSetup,
			trackOrigin: 'allhorizonmetrics',
			createdNow: false,
		});
	}

	cloneMetric(model: HorizonMetric) {
		const popup = this.mmMetricsService.createCustomMetricEditPopup();
		popup.open({
			model,
			origin: 'listing',
			isClone: true,
		});
		return popup;
	}

	private resolvedFilters: Record<
		string,
		Promise<APIData.HorizonMetrics.ResolveFiltersResponse>
	> = {};

	// sends a pick without values; cached for 1min per metric, field and operator
	// only used for ANY_OF / NONE_OF (value suggestions)
	resolveFilters(
		objectId: number,
		field: string,
		operator: MetricFilterOperator,
	): Promise<APIData.HorizonMetrics.ResolveFiltersResponse> {
		const op = operator === MetricFilterOperator.NONE_OF ? 'exclude' : 'in';
		const uniqueKey = `${objectId}|${field}|${op}`;

		let promise = this.resolvedFilters[uniqueKey];
		if (!promise) {
			promise = this.requestService.api<APIData.HorizonMetrics.ResolveFiltersResponse>(
				'POST',
				`/horizon-metrics/${objectId}/resolve/filters`,
				{ picks: { filters: [{ field, op }] } },
				{ skipShowingErrorInfolayer: true },
			);
			this.resolvedFilters[uniqueKey] = promise;

			void promise.then(
				() => {
					later(() => {
						delete this.resolvedFilters[uniqueKey];
					}, 60000);
				},
				() => {
					delete this.resolvedFilters[uniqueKey];
				},
			);
		}

		return promise;
	}

	private resolvedBreakdowns: Record<string, Promise<HorizonBreakdownOffer>> = {};

	// which splits the block may still add, and which of its picked splits Horizon refuses together;
	// cached for 1min per metric and selection
	resolveBreakdowns(objectId: number, breakdowns: string[]): Promise<HorizonBreakdownOffer> {
		const uniqueKey = `${objectId}|${breakdowns.join(',')}`;

		let promise = this.resolvedBreakdowns[uniqueKey];
		if (!promise) {
			promise = this.requestService
				.api<APIData.HorizonMetrics.ResolveFiltersResponse>(
					'POST',
					`/horizon-metrics/${objectId}/resolve/filters`,
					{ picks: { breakdowns: breakdowns.map((column) => ({ column })) } },
					{ skipShowingErrorInfolayer: true },
				)
				.then((response) => toBreakdownOffer(response, breakdowns));
			this.resolvedBreakdowns[uniqueKey] = promise;

			void promise.then(
				() => {
					later(() => {
						delete this.resolvedBreakdowns[uniqueKey];
					}, 60000);
				},
				() => {
					delete this.resolvedBreakdowns[uniqueKey];
				},
			);
		}

		return promise;
	}

	async getFilterValueOptions(
		objectId: number,
		field: string,
		operator: MetricFilterOperator,
	): Promise<LabelValuePairString[]> {
		const response = await this.resolveFilters(objectId, field, operator);
		// first match only: Horizon can offer two options (relations reached) for one field
		const option = response.filters.options?.find((item) => item.field === field);
		if (!option || option.unconfirmed) {
			return [];
		}
		return (option.valueOptions?.options || []).map(({ value, label }) => ({
			value: String(value),
			label,
		}));
	}

	// Horizon (vendor) values first, then warehouse values, else none (only typed values)
	// breakdowns: the block's splits; warehouse values live under the block's own config, so a field that is
	// one of several splits is asked first, followed by the other splits
	async getValueOptionsForField(
		metricSetup: MetricSetup,
		field: string,
		operator: MetricFilterOperator,
		dateRange?: DateRange,
		breakdowns: string[] = [],
	): Promise<LabelValuePairString[]> {
		const objectId = metricSetup.metric?.objectId;
		if (objectId) {
			try {
				const options = await this.getFilterValueOptions(objectId, field, operator);
				if (options.length) {
					return options;
				}
			} catch (_e) {
				// fall back to warehouse values
			}
		}

		let setupData = metricSetup.metricSetupDataForFetchDimensionValues;
		if (breakdowns.length > 1 && breakdowns.includes(field)) {
			const dimensions = [field, ...breakdowns.filter((key) => key !== field)];
			setupData = setupData.map((item) => ({ ...item, dimensions }));
		}

		try {
			const values = await this.mmMetricsService.getAttributes(setupData, dateRange);
			return values.map((value) => ({ value, label: value }));
		} catch (_e) {
			return [];
		}
	}

	purgeData(model: HorizonMetric): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.popupService.openConfirmPopup({
			onConfirm: () => {
				const name = model.metricName;
				const infolayer = this.infolayerService.loading(`Purging data for "${name}"...`);

				this.requestService
					.api('POST', `/horizon-metrics/${model.numericId}/purge`)
					.then(
						() => {
							infolayer.ok(`Data for "${name}" purged.`);
						},
						(errorData) => {
							infolayer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			},
			title: `Are you sure you want to purge data for the metric "${model.metricName}"?`,
			text: '<strong>This cannot be undone.</strong>',
			confirmAction: 'Purge',
			cancelAction: 'Cancel',
			confirmWord: 'PURGE',
		});
	}

	batchPurgeDataAction(
		items: HorizonMetric[],
		options?: BatchSelectActionOptions,
	): Promise<void> {
		const objectIds = items.map((item) => item.numericId!);
		return this.batchAction(BatchSelectActionType.PURGE_DATA, objectIds, undefined, options);
	}

	deleteMetric(model: HorizonMetric, onDeleted?: () => void): void {
		this.popupService.openConfirmPopup({
			onConfirm: () => {
				const name = model.metricName;
				const infolayer = this.infolayerService.loading(`Deleting "${name}"...`);
				model
					.delete()
					.then(
						() => {
							infolayer.ok(`Metric "${name}" deleted.`);
							onDeleted?.();
						},
						(errorData) => {
							infolayer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			},
			title: `Are you sure you want to delete the metric "${model.metricName}"?`,
			text: '',
		});
	}
}
